// Client for the user-alerts API (/api/anomaly-alerts).
//
// Deliberately separate from ./api.ts, which talks to the admin rule endpoints
// and passes hand-written metric_sql around. Nothing here sends SQL, a table
// name or a column name: creating an alert means confirming a draft the server
// already composed and validated. Keeping the two clients apart makes it
// obvious at a glance which surface is the admin one.
import { Timeframe } from './api';

export type AlertDirection = 'UP' | 'DOWN' | 'BOTH';

export interface AlertExplanation {
  ar: string | null;
  en: string | null;
}

export interface ResolvedFilter {
  column: string;
  value: string;
}

export interface UserAlert {
  id: number;
  table_name: string;
  rule_type: string;
  metric_name: string;
  timeframes: Timeframe[];
  direction: AlertDirection;
  severity: string;
  config: Record<string, unknown>;
  paused: boolean;
  source_text: string | null;
  source_lang: string | null;
  /** Rendered at create time and stored, so the card always shows the
   *  sentence the user actually agreed to. */
  explanation: AlertExplanation;
  resolved_filters: ResolvedFilter[];
  threshold_pct: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface AlertDraftSummary {
  table_name: string;
  rule_type: string;
  aggregate: string;
  measure_column: string | null;
  timeframe: Timeframe;
  direction: AlertDirection;
  threshold_pct: number | null;
  resolved_filters: ResolvedFilter[];
  label: string;
  source_lang: string | null;
}

export interface AlertPreview {
  status: 'ok';
  draft_id: number;
  explanation: { ar: string; en: string };
  summary: AlertDraftSummary;
}

/** A refusal or a disambiguation prompt — never an error string to dump raw. */
export interface AlertRefusal {
  status: 'refused' | 'needs_disambiguation';
  code: string;
  message_en: string;
  message_ar: string;
  suggestions: string[];
  choices: { column: string; value: string }[];
  /** Raw validator text. Only present for anomaly_admin callers. */
  detail?: string;
}

export class AlertRefusedError extends Error {
  refusal: AlertRefusal;
  constructor(refusal: AlertRefusal) {
    super(refusal.message_en);
    this.name = 'AlertRefusedError';
    this.refusal = refusal;
  }
}

async function jsonFetch(url: string, init?: RequestInit) {
  const res = await fetch(url, init);
  if (!res.ok) {
    const err = await res
      .json()
      .catch(() => ({ detail: `HTTP ${res.status}` }));
    // A refusal is a structured, translated, user-facing thing; a plain
    // detail string is an ordinary error. Distinguishing them here keeps the
    // raw validator text out of the UI.
    if (err.detail && typeof err.detail === 'object' && err.detail.code) {
      throw new AlertRefusedError(err.detail as AlertRefusal);
    }
    throw new Error(
      typeof err.detail === 'string' ? err.detail : `HTTP ${res.status}`
    );
  }
  return res.json();
}

const JSON_POST = (body: unknown): RequestInit => ({
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body)
});

export async function apiPreviewAlert(
  sentence: string,
  lang?: string
): Promise<AlertPreview> {
  return jsonFetch(
    '/api/anomaly-alerts/preview',
    JSON_POST({ sentence, lang })
  );
}

export async function apiCreateAlert(body: {
  draft_id: number;
  threshold_pct?: number;
  timeframe?: Timeframe;
  label?: string;
}): Promise<{ id: number; explanation: { ar: string; en: string } }> {
  return jsonFetch('/api/anomaly-alerts', JSON_POST(body));
}

export async function apiListAlerts(
  includePaused = true
): Promise<{ items: UserAlert[]; limit: number }> {
  return jsonFetch(`/api/anomaly-alerts?include_paused=${includePaused}`);
}

export async function apiAlertQuota(): Promise<{
  used: number;
  limit: number;
}> {
  return jsonFetch('/api/anomaly-alerts/quota');
}

export async function apiPauseAlert(id: number): Promise<void> {
  await jsonFetch(`/api/anomaly-alerts/${id}/pause`, { method: 'POST' });
}

export async function apiResumeAlert(id: number): Promise<void> {
  await jsonFetch(`/api/anomaly-alerts/${id}/resume`, { method: 'POST' });
}

export async function apiDeleteAlert(id: number): Promise<void> {
  await jsonFetch(`/api/anomaly-alerts/${id}`, { method: 'DELETE' });
}
